'use client'

import { useCallback, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { TiptapAiEditor } from '@/tiptap'
// --- Lib ---
import type { StreamContentAPI } from '@/tiptap'
import { renderToMarkdown } from '@/tiptap/markdown'
import { Editor } from '@tiptap/react'
import {
  createStructuredStream,
  createTextStream,
  createVercelAIStream,
  type StreamingChunkOptions,
  type StructuredContentBlock,
} from '@/lib/streaming-utils'
import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@deepguide-ai/ui'
// Using the new streamContent API from the streaming extension
import playgroundContent from '../-mock-data/playground-tiptap.components.json'
import { renderToMarkdownReadable } from '@/tiptap/markdown/renderToMarkdown'
import articleContent from '../-mock-data/article.tiptap.json'

export const Route = createFileRoute('/demo/tiptap/')({
  component: TiptapStreamingEditorDemo,
})

function TiptapStreamingEditorDemo() {
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamProgress, setStreamProgress] = useState(0)
  const [selectedMode, setSelectedMode] =
    useState<StreamingChunkOptions['mode']>('word')
  const [selectedSpeed, setSelectedSpeed] = useState<
    'slow' | 'normal' | 'fast'
  >('fast')
  const [_useMockAI, _setUseMockAI] = useState(true) // Default to mock for AI commands
  const editorRef = useRef<Editor | null>(null)
  const streamingRef = useRef<any>(null)

  // Get delay based on mode and speed
  const getDelay = (
    mode: StreamingChunkOptions['mode'],
    speed: 'slow' | 'normal' | 'fast'
  ) => {
    const baseDelays = {
      character: { slow: 100, normal: 50, fast: 20 },
      word: { slow: 300, normal: 150, fast: 80 },
      sentence: { slow: 800, normal: 500, fast: 200 },
      'fixed-length': { slow: 400, normal: 200, fast: 100 },
      block: { slow: 600, normal: 300, fast: 150 },
    }
    return baseDelays[mode][speed]
  }

  const stopStreaming = () => {
    if (streamingRef.current) {
      streamingRef.current.stopped = true
    }
    // No need to manually stop streaming - streamContent handles it automatically
    setIsStreaming(false)
  }

  const clearEditor = () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    console.log('🧹 Clearing editor content')
    editorRef.current.commands.setContent('')
    console.log(
      '>>> Cleared editor pos:',
      editorRef.current.state.doc.content.size
    )
  }

  const testBasicInsertion = () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    clearEditor()
    // Test with simple content first
    const simpleContent = {
      type: 'doc',
      content: [
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Test Heading' }],
        },
        {
          type: 'paragraph',
          content: [{ type: 'text', text: 'Hello, this is a test!' }],
        },
      ],
    }

    console.log('🧪 Testing basic content insertion:', simpleContent)
    editorRef.current.commands.setContent(simpleContent)
  }

  const testArticleContent = () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    clearEditor()
    editorRef.current.commands.setContent(articleContent)
  }


  const testDirectStreaming = async () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }
    clearEditor()
    console.log('🔬 Testing direct streamContent API...')

    // Test streamContent API with simple text
    try {
      const result = await editorRef.current.commands.streamContent(
        0,
        async ({ write }: StreamContentAPI) => {
          // Simulate streaming some text chunks
          const chunks = [
            'Hello, ',
            'this is ',
            'a test ',
            'of the ',
            'streamContent ',
            'API!',
          ]

          for (let i = 0; i < chunks.length; i++) {
            console.log(
              `Writing chunk ${i + 1}/${chunks.length}: "${chunks[i]}"`
            )
            await write({ partial: chunks[i], contentType: 'text' })

            // Add small delay between chunks to see the streaming effect
            await new Promise((resolve) => setTimeout(resolve, 200))
          }
        }
      )

      console.log('✅ StreamContent test result:', result)
    } catch (error) {
      console.error('❌ StreamContent test error:', error)
    }
  }

  // === NEW UNIFIED STREAMING TESTS USING UTILITY ===

  const testUnifiedTextStreaming = async () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    console.log(
      `🧪 Testing unified ${selectedMode} streaming at ${selectedSpeed} speed...`
    )
    setIsStreaming(true)
    setStreamProgress(0)

    try {
      const endPosition = editorRef.current.state.doc.content.size
      const sampleText =
        'This is a comprehensive test of the unified streaming utility. It demonstrates how different chunking modes work with various configurations. The utility supports character-by-character streaming, word-by-word streaming, sentence-based streaming, fixed-length chunks, and custom block separators!'

      // Configure streaming options based on selected mode and speed
      const streamingOptions: StreamingChunkOptions = {
        mode: selectedMode,
        delay: getDelay(selectedMode, selectedSpeed),
        ...(selectedMode === 'fixed-length' && { chunkSize: 10 }),
        ...(selectedMode === 'word' && { wordsPerChunk: 1 }),
        ...(selectedMode === 'block' && {
          blockSeparators: ['. ', '! ', '? '],
        }),
      }

      await editorRef.current.commands.streamContent(
        endPosition,
        async ({ write }: StreamContentAPI) => {
          // Create the text stream using our utility
          const textStream = createTextStream(sampleText, streamingOptions)
          const reader = textStream.getReader()

          try {
            while (true) {
              if (streamingRef.current?.stopped) {
                console.log('🛑 Unified streaming stopped by user')
                break
              }

              const { done, value } = await reader.read()

              if (done) break

              console.log(
                `🧪 ${selectedMode} chunk ${value.index + 1}/${value.total}: "${value.content.slice(-20)}"`
              )
              console.log(`🧪 Progress: ${value.progress}%`)

              write({ partial: value.content, contentType: 'text' })
              setStreamProgress(value.progress)
            }
          } finally {
            reader.releaseLock()
          }
        }
      )

      console.log(`✅ Unified ${selectedMode} streaming complete`)
    } catch (error) {
      console.error(`❌ Unified ${selectedMode} streaming error:`, error)
    } finally {
      setIsStreaming(false)
      setStreamProgress(100)
    }
  }

  const testUnifiedStructuredStreaming = async () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    // Only use modes that work with structured content
    const textMode =
      selectedMode === 'character' ||
      selectedMode === 'word' ||
      selectedMode === 'sentence'
        ? selectedMode
        : 'word'

    console.log(
      `🧪 Testing unified structured streaming with ${textMode} mode at ${selectedSpeed} speed...`
    )
    setIsStreaming(true)
    setStreamProgress(0)

    try {
      const endPosition = editorRef.current.state.doc.content.size
      console.log('>>> endPosition:', endPosition)
      // Define structured content blocks
      const contentBlocks: StructuredContentBlock[] = [
        {
          type: 'heading',
          attrs: { level: 1 },
          text: 'Unified Streaming Demo',
        },
        {
          type: 'paragraph',
          text: 'This demonstrates the new unified streaming utility that supports multiple chunking modes.',
        },
        {
          type: 'heading',
          attrs: { level: 2 },
          text: 'Features',
        },
        {
          type: 'paragraph',
          text: 'The utility provides character-by-character, word-by-word, sentence-based, fixed-length, and block-based streaming modes.',
        },
      ]

      await editorRef.current.commands.streamContent(
        endPosition,
        async ({ write }: StreamContentAPI) => {
          // Create the structured stream using our utility
          const structuredStream = createStructuredStream(contentBlocks, {
            textMode,
            delay: getDelay(textMode, selectedSpeed),
          })

          const reader = structuredStream.getReader()

          try {
            while (true) {
              if (streamingRef.current?.stopped) {
                console.log('🛑 Unified structured streaming stopped by user')
                break
              }

              const { done, value } = await reader.read()

              if (done) break

              console.log(
                `🧪 Structured chunk ${value.index + 1}/${value.total} (${value.progress}%)`
              )

              write({ partial: value.content, contentType: 'json' })
              setStreamProgress(value.progress)
            }
          } finally {
            reader.releaseLock()
          }
        }
      )

      console.log(`✅ Unified structured streaming complete`)
    } catch (error) {
      console.error(`❌ Unified structured streaming error:`, error)
    } finally {
      setIsStreaming(false)
      setStreamProgress(100)
    }
  }

  const testVercelAIStreamFormat = async () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    console.log(
      `🧪 Testing Vercel AI SDK format streaming with ${selectedMode} mode at ${selectedSpeed} speed...`
    )
    setIsStreaming(true)
    setStreamProgress(0)

    try {
      const endPosition = editorRef.current.state.doc.content.size
      const sampleText =
        'This simulates a real AI response stream using the Vercel AI SDK format. Each chunk is properly formatted as a data stream message.'

      // Create Vercel AI SDK format stream using selected options
      const vercelStream = createVercelAIStream(sampleText, {
        mode: selectedMode,
        delay: getDelay(selectedMode, selectedSpeed),
        ...(selectedMode === 'fixed-length' && { chunkSize: 10 }),
        ...(selectedMode === 'word' && {
          wordsPerChunk: selectedSpeed === 'fast' ? 2 : 1,
        }),
        ...(selectedMode === 'block' && {
          blockSeparators: ['. ', '! ', '? '],
        }),
      })

      await editorRef.current.commands.streamContent(
        endPosition,
        async ({ write }: StreamContentAPI) => {
          const reader = vercelStream.getReader()
          const decoder = new TextDecoder()

          try {
            while (true) {
              if (streamingRef.current?.stopped) {
                console.log('🛑 Vercel AI streaming stopped by user')
                break
              }

              const { done, value } = await reader.read()

              if (done) break

              const chunk = decoder.decode(value, { stream: true })
              console.log(`🧪 Vercel AI chunk:`, chunk.slice(0, 50))

              // Parse Vercel AI SDK format (same as your existing AI extension)
              const lines = chunk.split('\n')
              for (const line of lines) {
                if (line.startsWith('0:')) {
                  const textData = line.slice(2)
                  let text: string

                  try {
                    text = JSON.parse(textData)
                  } catch {
                    text = textData
                  }

                  write({ partial: text, contentType: 'text' })
                }
              }
            }
          } finally {
            reader.releaseLock()
          }
        }
      )

      console.log('✅ Vercel AI SDK format streaming complete')
    } catch (error) {
      console.error('❌ Vercel AI SDK format streaming error:', error)
    } finally {
      setIsStreaming(false)
      setStreamProgress(100)
    }
  }

  const streamPlaygroundContent = async () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    console.log(
      `🎮 Streaming playground content with ${selectedMode} mode at ${selectedSpeed} speed...`
    )
    setIsStreaming(true)
    setStreamProgress(0)
    console.log('🎯 Setting streaming to TRUE')

    // Small delay to ensure UI updates are visible
    await new Promise((resolve) => setTimeout(resolve, 100))

    const endPosition = editorRef.current.state.doc.content.size
    const contentArray = playgroundContent.content || []

    if (contentArray.length === 0) {
      console.warn('No playground content found')
      setIsStreaming(false)
      return
    }

    console.log(`📦 Found ${contentArray.length} playground content objects`)

    editorRef.current.commands.streamContent(
      endPosition,
      async ({ write }: StreamContentAPI) => {
        try {
          // Stream the full playground JSON using selected settings
          const fullJson = JSON.stringify(playgroundContent)

          const streamingOptions: StreamingChunkOptions = {
            mode: selectedMode,
            delay: getDelay(selectedMode, selectedSpeed),
            ...(selectedMode === 'fixed-length' && { chunkSize: 20 }), // Larger chunks for JSON
            ...(selectedMode === 'word' && { wordsPerChunk: 1 }),
            ...(selectedMode === 'block' && {
              blockSeparators: ['},', ']', '['],
            }),
          }

          const textStream = createTextStream(fullJson, streamingOptions)
          const reader = textStream.getReader()

          try {
            while (true) {
              if (streamingRef.current?.stopped) {
                console.log('🛑 Playground streaming stopped by user')
                break
              }

              const { done, value } = await reader.read()

              if (done) break

              console.log(
                `🎮 Playground chunk ${value.index + 1}/${value.total} (${value.progress}%): ${value.content}`
              )

              write({ partial: value.content, contentType: 'json' })
              setStreamProgress(value.progress)
            }
          } finally {
            reader.releaseLock()
          }

          console.log('✅ Playground content streaming complete')
        } catch (error) {
          console.error('❌ Playground streaming error:', error)
        } finally {
          console.log('🎯 Setting streaming to FALSE from callback')
          setIsStreaming(false)
          setStreamProgress(0)
        }
      }
    )
  }

  const testChainAndScrollIntoView = async () => {
    if (!editorRef.current) {
      console.error('Editor not available')
      return
    }

    console.log('🔗 Testing chain commands and scrollIntoView...')
    setIsStreaming(true)
    setStreamProgress(0)

    // Small delay to ensure UI updates are visible
    await new Promise((resolve) => setTimeout(resolve, 100))

    const endPosition = editorRef.current.state.doc.content.size

    editorRef.current.commands.streamContent(
      { from: endPosition, to: endPosition },
      async ({ write }: StreamContentAPI) => {
        try {
          // Create a long text to test scrolling
          const longText = `
🔗 Testing Chain Commands with ScrollIntoView

This is a test of the chain ability in the streamContent API. We're going to write a lot of content to test the scrollIntoView functionality.

Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.

Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.

Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.

Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.

🎯 This should automatically scroll into view!
        `.trim()

          // Split into words for streaming
          const words = longText.split(/(\s+)/)
          const delay = getDelay('word', selectedSpeed)

          for (let i = 0; i < words.length; i++) {
            if (streamingRef.current?.stopped) {
              console.log('🛑 Chain test streaming stopped by user')
              break
            }

            const word = words[i]
            console.log(`🔗 Writing word ${i + 1}/${words.length}: "${word}"`)

            // Test different chain approaches
            if (i % 20 === 0) {
              // Every 20th word, test simple chain command
              write({
                partial: word,
                contentType: 'text',
                appendToChain: (chain) => {
                  console.log('🔗 Testing simple chain command')
                  try {
                    // Try the simplest possible chain command
                    return chain.focus()
                  } catch (chainError) {
                    console.error('❌ Chain error:', chainError)
                    return chain
                  }
                },
              })
            } else if (i % 10 === 0) {
              // Every 10th word, test scrollIntoView
              write({
                partial: word,
                contentType: 'text',
                appendToChain: (chain) => {
                  console.log('🔗 Testing scrollIntoView chain')
                  try {
                    return chain.scrollIntoView()
                  } catch (scrollError) {
                    console.error('❌ ScrollIntoView error:', scrollError)
                    return chain
                  }
                },
              })
            } else {
              // Regular write without chain
              write({
                partial: word,
                contentType: 'text',
              })
            }

            // Update progress
            const progress = Math.round(((i + 1) / words.length) * 100)
            setStreamProgress(progress)

            await new Promise((resolve) => setTimeout(resolve, delay))
          }

          console.log('✅ Chain and scrollIntoView test complete')
        } catch (error) {
          console.error('❌ Chain test error:', error)
        } finally {
          console.log('🎯 Setting streaming to FALSE from chain test callback')
          setIsStreaming(false)
          setStreamProgress(0)
        }
      }
    )
  }

  const handleEditorCreated = useCallback(({ editor }: { editor: Editor }) => {
    editorRef.current = editor
    console.log('Editor created:', editor)
  }, [])

  return (
    <>
      <div className='flex h-screen flex-row'>
        {/* Stream Controls - Left Panel */}
        <div className='w-1/2 flex-shrink-0 space-y-3 overflow-y-auto border-r border-gray-300 p-4'>
          <div className='rounded-sm border border-blue-200 bg-blue-50 p-3 text-sm text-gray-700'>
            🚀 Select a chunking mode and speed, then test any streaming
            function. All buttons honor your selection!
          </div>

          {/* Mode and Speed Selection */}
          <div className='flex items-center gap-4 rounded-md border bg-gray-50 p-4'>
            <div className='flex items-center gap-2'>
              <label className='text-sm font-medium text-gray-700'>
                Chunking Mode:
              </label>
              <select
                value={selectedMode}
                onChange={(e) =>
                  setSelectedMode(
                    e.target.value as StreamingChunkOptions['mode']
                  )
                }
                disabled={isStreaming}
                className='rounded-sm border border-gray-300 px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100'
              >
                <option value='character'>Character-by-Character</option>
                <option value='word'>Word-by-Word</option>
                <option value='sentence'>Sentence-by-Sentence</option>
                <option value='fixed-length'>Fixed-Length Chunks</option>
                <option value='block'>Block-based</option>
              </select>
            </div>

            <div className='flex items-center gap-2'>
              <label className='text-sm font-medium text-gray-700'>
                Speed:
              </label>
              <select
                value={selectedSpeed}
                onChange={(e) =>
                  setSelectedSpeed(e.target.value as 'slow' | 'normal' | 'fast')
                }
                disabled={isStreaming}
                className='rounded-sm border border-gray-300 px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100'
              >
                <option value='slow'>Slow (Readable)</option>
                <option value='normal'>Normal</option>
                <option value='fast'>Fast (AI-like)</option>
              </select>
            </div>

            <div className='text-muted-foreground text-sm'>
              Current: <strong>{selectedMode}</strong> at{' '}
              <strong>{selectedSpeed}</strong> speed
            </div>
          </div>

          {/* Streaming Test Buttons */}
          <div className='space-y-3'>
            {/* Text Streaming Tests */}
            <div className='rounded-sm border border-green-200 bg-green-50 p-3'>
              <div className='mb-2 text-sm font-semibold text-status-green-strong'>
                📝 Text Streaming Tests
              </div>
              <div className='flex flex-wrap gap-2'>
                <button
                  onClick={() => {
                    streamingRef.current = { stopped: false }
                    clearEditor()
                    testUnifiedTextStreaming()
                  }}
                  disabled={isStreaming}
                  className='rounded-sm bg-status-green px-3 py-2 text-sm text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400'
                  title='Stream plain text using the selected chunking mode and speed'
                >
                  📝 Text Stream
                </button>

                <button
                  onClick={() => {
                    streamingRef.current = { stopped: false }
                    clearEditor()
                    testVercelAIStreamFormat()
                  }}
                  disabled={isStreaming}
                  className='rounded-sm bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400'
                  title='Stream text in Vercel AI SDK format (simulates real AI responses)'
                >
                  🤖 Vercel AI Format Stream
                </button>

                <button
                  onClick={() => {
                    streamingRef.current = { stopped: false }
                    clearEditor()
                    testChainAndScrollIntoView()
                  }}
                  disabled={isStreaming}
                  className='rounded-sm bg-orange-600 px-3 py-2 text-sm text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-400'
                  title='Test chain commands with automatic cursor positioning and scrollIntoView'
                >
                  🔗 Chain & Scroll Test
                </button>
              </div>
            </div>

            {/* Structured Content Tests */}
            <div className='rounded-sm border border-blue-200 bg-blue-50 p-3'>
              <div className='mb-2 text-sm font-semibold text-status-blue-strong'>
                🏗️ Structured Content Tests
              </div>
              <div className='flex flex-wrap gap-2'>
                <button
                  onClick={() => {
                    streamingRef.current = { stopped: false }
                    clearEditor()
                    testUnifiedStructuredStreaming()
                  }}
                  disabled={isStreaming}
                  className='rounded-sm bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400'
                  title='Stream structured JSON content (headings, paragraphs) with word-by-word text buildup'
                >
                  🏗️ Stream Test JSON
                </button>

                <button
                  onClick={() => {
                    streamingRef.current = { stopped: false }
                    clearEditor()
                    streamPlaygroundContent()
                  }}
                  disabled={isStreaming}
                  className='rounded-sm bg-purple-600 px-3 py-2 text-sm text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-400'
                  title='Stream the actual playground content using selected mode and speed'
                >
                  🎮 Stream Playground Content
                </button>
              </div>
            </div>

            {/* Utility Buttons */}
            <div className='rounded-sm border border-red-200 bg-red-50 p-3'>
              <div className='mb-2 text-sm font-semibold text-status-red-strong'>
                🛠️ Utility Functions
              </div>
              <div className='flex flex-wrap gap-2'>
                <button
                  onClick={stopStreaming}
                  disabled={!isStreaming}
                  className='rounded-sm bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400'
                  title='Stop any currently running stream'
                >
                  ⏹️ Stop Stream
                </button>

                <button
                  onClick={clearEditor}
                  className='rounded-sm bg-destructive px-3 py-2 text-sm text-white hover:bg-red-600'
                  title='Clear all content from the editor'
                >
                  🗑️ Clear Editor
                </button>

                <button
                  onClick={testBasicInsertion}
                  className='rounded-sm bg-yellow-600 px-3 py-2 text-sm text-white hover:bg-yellow-700'
                  title='Test basic content insertion (non-streaming)'
                >
                  📄 Test Simple Content
                </button>

                <button
                  onClick={testArticleContent}
                  className='rounded-sm bg-yellow-600 px-3 py-2 text-sm text-white hover:bg-yellow-700'
                  title='Test basic content insertion (non-streaming)'
                >
                  📄 Test Article Content
                </button>

                <button
                  onClick={testDirectStreaming}
                  className='rounded-sm bg-orange-600 px-3 py-2 text-sm text-white hover:bg-orange-700'
                  title='Test direct streaming API'
                >
                  🔧 Test Direct Streaming
                </button>
              </div>
            </div>
          </div>

          {isStreaming && (
            <div className='flex-1'>
              <div className='flex items-center gap-2'>
                <div className='h-2 flex-1 rounded-full bg-gray-200'>
                  <div
                    className='h-2 rounded-full bg-blue-600 transition-all duration-300'
                    style={{ width: `${streamProgress}%` }}
                  />
                </div>
                <span className='text-muted-foreground text-sm'>
                  {streamProgress}%
                </span>
              </div>
            </div>
          )}

          <div className='text-foreground text-sm'>
            Status: {isStreaming ? '🔄 Streaming' : '⏹️ Stopped'} | Mode:{' '}
            <strong>{selectedMode}</strong> | Speed:{' '}
            <strong>{selectedSpeed}</strong> | All buttons use your selected
            settings!
          </div>

          <div className='flex flex-row gap-2'>
            <ThemeToggle />
            <Button
              onClick={() => {
                console.log(editorRef.current?.getJSON())
              }}
            >
              Print Content
            </Button>
            <Button
              onClick={() => {
                console.log(renderToMarkdown(editorRef.current?.getJSON() as any))
              }}
            >
              Print MDX
            </Button>
            <Button
              onClick={() => {
                console.log(renderToMarkdownReadable(editorRef.current?.getJSON() as any))
              }}
            >
              Print Markdown
            </Button>
          </div>
        </div>

        {/* Editor - Right Panel */}
        <div className='tiptap-ai-editor-container mx-auto min-h-0 max-w-[710px] px-10'>
          <TiptapAiEditor
            content={playgroundContent}
            onCreate={handleEditorCreated}
          />
        </div>
      </div>
    </>
  )
}
